import { Component, ViewChild, ElementRef, OnInit, Inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';

import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatIconButton, MatFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatNavList, MatListItem } from '@angular/material/list';

// Define an interface for audio file data
interface AudioFile {
  id: number;
  title: string;
  audioUrl: string; // This will point to a simulated PHP endpoint
}

@Component({
  selector: 'app-audio-library',
  templateUrl: './audio-library.component.html',
  styleUrls: ['./audio-library.component.css'],
  imports: [MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatSlider, MatSliderThumb, MatIconButton, MatTooltip, MatIcon, MatFabButton, MatNavList, MatListItem]
})
export class AudioLibraryComponent implements OnInit {


  @ViewChild('audioElement', { static: false }) audioElementRef!: ElementRef<HTMLAudioElement>;

  audioFiles: AudioFile[] = [
    { id: 4, title: 'Commitment Of Chair', audioUrl: 'assets/audio/CommitmentofChair.m4a' },
    { id: 1, title: 'Common Points Of Order', audioUrl: 'assets/audio/CommonPointsofOrder.m4a' },
    { id: 2, title: 'Duties Of Chair', audioUrl: 'assets/audio/DutiesofChair.m4a' },
    { id: 3, title: 'Important Rules', audioUrl: 'assets/audio/ImportantRules.m4a' },
    { id: 5, title: 'Historical Quotes', audioUrl: 'assets/audio/Quotes.m4a' },
    { id: 6, title: 'Language For Chair', audioUrl: 'assets/audio/LanguageForChair.m4a' },
    { id: 7, title: 'Member Responsibilities', audioUrl: 'assets/audio/MemberResponsibilities.m4a' },
    { id: 8, title: 'Member Rights', audioUrl: 'assets/audio/MemberRights.m4a' },
    { id: 9, title: 'Prohibited Actions', audioUrl: 'assets/audio/ProhibitedActions.m4a' },
    { id: 10, title: 'Meeting CheatSheet', audioUrl: 'assets/audio/MeetingCheatSheet.m4a' },
    { id: 11, title: 'Motion Precedence', audioUrl: 'assets/audio/MotionPrecedence.m4a' }
  ];


  videos: boolean = false;

  selectedAudio: AudioFile | null = null;
  isPlaying: boolean = false;
  currentTime: number = 0;
  duration: number = 0;

  isBrowser = false;

  constructor(private auth: AuthService, private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object, private cdr: ChangeDetectorRef) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }


  ngOnInit(): void {
    if (this.isBrowser) {
      this.http.post<boolean>('api/members/ownsVideo.php', { email: this.auth.currentUser.email }).subscribe({
        next: (response) => {
          this.videos = response;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error checking video ownership:', error);
          this.videos = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      // avoid server-side HTTP during prerender
      this.videos = false;
    }


    // TEST FOR LOGGED IN AND OWN AT LEAST ONE VIDEO
    if (this.isLoggedIn()) {

    }

  }

  isLoggedIn() {
    return this.auth.isLoggedIn();
  }

  ownsVideo() {
    return this.videos;
  }

  /**
   * Handles selection of an audio file from the list.
   * @param audio The audio file selected.
   */
  onSelectAudio(audio: AudioFile): void {
    this.selectedAudio = audio;
    this.isPlaying = false; // Reset play state
    this.currentTime = 0; // Reset time
    this.duration = 0; // Reset duration

    // Use a small timeout to ensure the audio element is rendered and ready
    setTimeout(() => {
      if (this.audioElementRef && this.audioElementRef.nativeElement) {
        const audioEl = this.audioElementRef.nativeElement;
        audioEl.load(); // Load the new audio source
        audioEl.onloadedmetadata = () => {
          this.duration = audioEl.duration;
          this.cdr.detectChanges();
        };
        audioEl.ontimeupdate = () => {
          this.currentTime = audioEl.currentTime;
          this.cdr.detectChanges();
        };
        audioEl.onended = () => {
          this.isPlaying = false;
          this.currentTime = 0;
          this.cdr.detectChanges();
        };
        this.togglePlayPause(); // Auto-play the selected audio
      }
    }, 0);
  }

  /**
   * Toggles play/pause state of the audio.
   */
  togglePlayPause(): void {


    if (!this.isLoggedIn() || !this.ownsVideo()) {
      return;
    }

    if (!this.selectedAudio) return;

    const audioEl = this.audioElementRef.nativeElement;
    if (audioEl.paused) {
      audioEl.play();
      this.isPlaying = true;
    } else {
      audioEl.pause();
      this.isPlaying = false;
    }
  }

  /**
   * Skips forward by a specified number of seconds.
   * @param seconds The number of seconds to fast forward.
   */
  fastForward(seconds: number): void {
    if (!this.selectedAudio) return;
    const audioEl = this.audioElementRef.nativeElement;
    audioEl.currentTime = Math.min(audioEl.duration, audioEl.currentTime + seconds);
  }

  /**
   * Skips backward by a specified number of seconds.
   * @param seconds The number of seconds to rewind.
   */
  rewind(seconds: number): void {
    if (!this.selectedAudio) return;
    const audioEl = this.audioElementRef.nativeElement;
    audioEl.currentTime = Math.max(0, audioEl.currentTime - seconds);
  }

  /**
   * Formats time from seconds to a readable MM:SS string.
   * @param seconds The time in seconds.
   * @returns Formatted time string.
   */
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }


  // --- NEW SPEED CONTROL PROPERTIES ---
  playbackSpeeds: number[] = [0.75, 1.0, 1.25, 1.5, 1.75, 2.0]; // Define your desired speed options
  currentSpeedIndex: number = 1; // Index of the default speed (1.0 in this case)
  currentPlaybackSpeed: number = this.playbackSpeeds[this.currentSpeedIndex];

  // A computed property for display purposes in the template
  get currentPlaybackSpeedDisplay(): string {
    return `${this.currentPlaybackSpeed}x`;
  }

  cyclePlaybackSpeed(): void {
    if (!this.selectedAudio) {
      return; // Do nothing if no audio is selected
    }

    // Move to the next speed in the array, loop back to the start if at the end
    this.currentSpeedIndex = (this.currentSpeedIndex + 1) % this.playbackSpeeds.length;
    this.currentPlaybackSpeed = this.playbackSpeeds[this.currentSpeedIndex];

    // Apply the new speed to the audio element
    if (this.audioElementRef && this.audioElementRef.nativeElement) {
      this.audioElementRef.nativeElement.playbackRate = this.currentPlaybackSpeed;

    }
  }

  ngAfterViewInit(): void {
    if (this.audioElementRef && this.audioElementRef.nativeElement) {
      // Set initial speed when audio player is ready
      this.audioElementRef.nativeElement.playbackRate = this.currentPlaybackSpeed;

      // Optional: Update speed indicator if external changes occur (e.g., user changes speed in browser controls)
      this.audioElementRef.nativeElement.addEventListener('ratechange', () => {
        const currentRate = this.audioElementRef.nativeElement.playbackRate;
        // Find the closest predefined speed or just update the currentPlaybackSpeed directly
        const closestSpeed = this.playbackSpeeds.reduce((prev, curr) =>
          Math.abs(curr - currentRate) < Math.abs(prev - currentRate) ? curr : prev
        );
        this.currentPlaybackSpeed = closestSpeed;
        this.currentSpeedIndex = this.playbackSpeeds.indexOf(closestSpeed);
      });
    }

  }
}